/**
 * Header — чёрный топ-бар на всю ширину (redesign по макету «Главная»):
 * лого · сегментированные вкладки разделов · поиск по показателям · год ·
 * меню пользователя. Поиск — UI-заглушка до появления API.
 *
 * Планшеты: в полном размере лого (313 px), вкладки с подписями (532 px)
 * и правый блок (230 px) не влезают ни в 768, ни в 1024 px — подписи
 * вкладок наезжали на селектор года. До `xl` вкладки остаются иконками
 * (подпись — `sr-only` + `title`: доступна скринридеру и по наведению), а
 * лента вкладок прокручивается, если не хватает даже на иконки (телефоны).
 *
 * Селектор года пишет выбор в YearContext: страницы передают его
 * параметром year в запросы к бэку; пункт «Последние данные» (null) —
 * без параметра, бэк отдаёт последний доступный год (FR-7).
 */
import {
  ArrowLeftRight,
  Check,
  LogOut,
  User as UserIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';

import { ReactComponent as AnalyticsIcon } from '../../assets/icons/analytics.svg';
import { ReactComponent as DpIcon } from '../../assets/icons/dp.svg';
import { ReactComponent as MapIcon } from '../../assets/icons/map.svg';
import { ReactComponent as ProjectsIcon } from '../../assets/icons/projects.svg';

import { useKato } from '../../contexts/KatoContext';
import { useUser } from '../../contexts/UserContext';
import { useYear } from '../../contexts/YearContext';
import { useKeycloak } from '../../keycloak/KeycloakProvider';
import { cn } from '../../lib/cn';
import { canEditData } from '../../lib/user-access';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
// Tooltip/TooltipTrigger/TooltipContent понадобятся снова, когда вернётся
// закомментированный поиск по показателям, — сейчас их в разметке нет.
import { TooltipProvider } from '../ui/tooltip';
import { Logo } from './Logo';

const DEFAULT_KATO = 150000000;

const tabBase =
  'flex h-10 shrink-0 items-center gap-0 whitespace-nowrap px-3 text-xs font-bold uppercase tracking-wide transition-colors xl:gap-2 xl:px-4';

/** Подпись вкладки: до `xl` доступна только скринридеру (иконки-вкладки). */
const tabLabel = 'sr-only xl:not-sr-only';

export const Header = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { selectedKato } = useKato();
  const { selectedYear, setSelectedYear } = useYear();
  const { keycloak } = useKeycloak();

  // DEFAULT_KATO — областной КАТО Актобе, то есть на любом другом стенде он
  // ведёт в чужой регион. Своя территория пользователя точно из этого региона,
  // поэтому она идёт первой; константа остаётся только для анонима.
  const dpKato =
    selectedKato ?? (user?.kato_access ?? []).find((c) => c !== 0) ?? DEFAULT_KATO;
  // Не «есть КАТО-роль», а «есть право ввода»: территория и право записи —
  // разные вопросы (DP-348), иначе «только просмотр» видит вкладку и получает
  // 403 на сохранении.
  const canInputData = canEditData(user);
  const isAdmin = user?.is_admin === true;
  const currentYear = new Date().getFullYear();
  // Годы селектора: текущий и шесть предыдущих.
  const yearOptions = Array.from({ length: 3 }, (_, i) => currentYear - i);

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      tabBase,
      isActive
        ? 'bg-brand-500 text-white'
        : 'bg-white text-text hover:bg-surface-muted',
    );

  return (
    <header className="flex h-16 shrink-0 items-center gap-3 bg-[#0F1512] px-3 xl:gap-5 xl:px-4">
      <Logo />

      <TooltipProvider delayDuration={150}>
        {/* Лента вкладок прокручивается, если не влезает (телефоны,
            длинные казахские подписи), — вместо наезда на правый блок. */}
        <div className="-mx-1 min-w-0 flex-1 overflow-x-auto p-1">
          <nav className="flex w-max items-center divide-x divide-surface-border overflow-hidden rounded-lg">
            <NavLink
              to="/"
              end
              className={tabClass}
              title={t('topTab.home')}
            >
              <MapIcon className="size-4 shrink-0" />
              <span className={tabLabel}>{t('topTab.home')}</span>
            </NavLink>
            <NavLink
              to={`/${dpKato}/digital-passport`}
              className={tabClass}
              title={t('topTab.digitalPassport')}
            >
              <DpIcon className="size-4 shrink-0" />
              <span className={tabLabel}>{t('topTab.digitalPassport')}</span>
            </NavLink>
            <NavLink
              to="/analytics"
              className={tabClass}
              title={t('topTab.analytics')}
            >
              <AnalyticsIcon className="size-4 shrink-0" />
              <span className={tabLabel}>{t('topTab.analytics')}</span>
            </NavLink>
            <NavLink
              to="/projects"
              className={tabClass}
              title={t('topTab.projects')}
            >
              <ProjectsIcon className="size-4 shrink-0" />
              <span className={tabLabel}>{t('topTab.projects')}</span>
            </NavLink>
          </nav>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 xl:gap-3">
          {/* <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex h-10 w-[320px] min-w-[140px] shrink items-center gap-2.5 rounded-lg border border-white/10 bg-white/[0.07] px-3.5">
                <Search className="size-4 shrink-0 text-white/40" />
                <input
                  type="search"
                  disabled
                  placeholder={t('header.searchPlaceholder')}
                  className="w-full cursor-not-allowed bg-transparent text-sm text-white outline-none placeholder:text-white/40"
                />
                <Sparkles className="size-4 shrink-0 text-brand-500" />
              </div>
            </TooltipTrigger>
            <TooltipContent>{t('topTab.inDevelopment')}</TooltipContent>
          </Tooltip> */}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t('header.year')}
                data-testid="header-year"
                className="flex h-10 items-center gap-2 rounded-lg border border-white/15 px-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10"
              >
                {/* Год не выбран (null) — режим «Последние данные»:
                    показываем его и по умолчанию, и после выбора пункта. */}
                <span className="whitespace-nowrap tabular-nums">
                  {selectedYear ?? t('header.yearAuto')}
                </span>
                <ArrowLeftRight className="size-3.5 text-white/40" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* «Последние данные» — год не передаётся, бэк отдаёт
                  последний доступный (FR-7). */}
              <DropdownMenuItem
                data-testid="header-year-auto"
                onClick={() => setSelectedYear(null)}
              >
                <Check
                  className={cn(
                    'mr-2 size-4',
                    selectedYear === null ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {t('header.yearAuto')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {yearOptions.map((year) => (
                <DropdownMenuItem
                  key={year}
                  data-testid={`header-year-${year}`}
                  onClick={() => setSelectedYear(year)}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      selectedYear === year ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="tabular-nums">{year}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full text-white hover:bg-white/10 hover:text-white"
                aria-label={user?.name ?? 'user menu'}
              >
                <Avatar className="size-8">
                  <AvatarFallback className="bg-brand-50 text-brand-600">
                    <UserIcon className="size-4" />
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* Ввод данных виден супер-админу и ответственному лицу КАТО:
                  у остальных страница всё равно откажет в доступе. */}
              {canInputData && (
                <>
                  <DropdownMenuItem asChild>
                    <Link to="/admin/input-data">{t('adminInput.title')}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              {/* Управление видимостью витрины — только супер-админ (US-09 FR-1.1).
                  «Состав населения» остаётся скрытым до готовности раздела. */}
              {isAdmin && (
                <>
                  <DropdownMenuItem asChild>
                    <Link to="/admin/modules">{t('admin.modules')}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => keycloak?.logout()}>
                <LogOut className="mr-2 size-4" />
                {t('header.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TooltipProvider>
    </header>
  );
};
